import * as React from "react";

import { useCustomerCartId, useCustomerSession } from "../../../Common";

import {
  DataSourceMyBagQueryProviderProps,
  useDataSourceMyBagController,
} from "../data-source--mybag-provider";
import {
  DataSourceMyBagTotalsQueryProviderProps,
  useDataSourceMyBagTotalsController,
} from "../data-source--mybag-totals-provider";

import { useReactMyBagInitialState } from "../react-my-bag-initial-state-provider";

interface MyBagInitializationConsumerInterface {
  hasInitialized: boolean;
  isReady: boolean;
}
const defaultValue = {} as unknown as MyBagInitializationConsumerInterface;
const MyBagInitializationContext = React.createContext(defaultValue);

interface MyBagInitialization_ControllerConsumerInterface {
  handleMyBagAppearedSoKickoffTheInitialLoad: () => void;
}
const defaultValue_controller =
  {} as unknown as MyBagInitialization_ControllerConsumerInterface;
const MyBagInitialization_ControllerContext = React.createContext(
  defaultValue_controller
);

export interface MyBagInitializationProviderProps {
  providerId: string;
}
export const MyBagInitializationProvider: React.FunctionComponent<
  MyBagInitializationProviderProps
> = (props) => {
  const { isCustomerLoggedIn } = useCustomerSession();
  const { cartId } = useCustomerCartId();

  const { request: dataSourceMyBag_request, desiredCart: currentDesiredCart } =
    useDataSourceMyBagController();
  const { request: dataSourceMyBagTotals_request } =
    useDataSourceMyBagTotalsController();

  const [isReady, setIsReady] = React.useState(false);
  const [hasInitialized, setHasInitialized] = React.useState(false);

  const thisProviderEventNameBase = React.useMemo(
    () =>
      `bbnt-checkout-2022-my-bag-auto-initialization-provider-${props.providerId}`,
    [props.providerId]
  );

  const reinitializeEventKey = React.useMemo(
    () => `${thisProviderEventNameBase}--reinitialize`,
    [thisProviderEventNameBase]
  );
  const reinitialize = React.useCallback(() => {
    if (cartId || isCustomerLoggedIn) {
      const desiredCart:
        | DataSourceMyBagQueryProviderProps
        | DataSourceMyBagTotalsQueryProviderProps = {
        isCustomerLoggedIn,
        cartId,
        useInitialState: useReactMyBagInitialState,
      };
      dataSourceMyBag_request(desiredCart);
      dataSourceMyBagTotals_request(desiredCart);
    }

    setHasInitialized(true);
  }, [
    isCustomerLoggedIn,
    cartId,
    setHasInitialized,
    dataSourceMyBag_request,
    dataSourceMyBagTotals_request,
  ]);
  React.useEffect(() => {
    setIsReady(true);

    window.addEventListener(reinitializeEventKey, reinitialize);

    return () => {
      window.removeEventListener(reinitializeEventKey, reinitialize);
    };
  }, [setIsReady, reinitializeEventKey, reinitialize]);

  React.useEffect(() => {
    if (
      hasInitialized &&
      !!isCustomerLoggedIn !== !!currentDesiredCart?.isCustomerLoggedIn
    ) {
      window.dispatchEvent(new Event(reinitializeEventKey));
    }
  }, [hasInitialized, isCustomerLoggedIn, currentDesiredCart]);

  const handleMyBagAppearedSoKickoffTheInitialLoad: MyBagInitialization_ControllerConsumerInterface["handleMyBagAppearedSoKickoffTheInitialLoad"] =
    React.useCallback(() => {
      window.dispatchEvent(new Event(reinitializeEventKey));
    }, [reinitializeEventKey]);

  const value: MyBagInitializationConsumerInterface = React.useMemo(
    () => ({
      hasInitialized,
      isReady,
    }),
    [hasInitialized, isReady]
  );
  const controllerValue: MyBagInitialization_ControllerConsumerInterface =
    React.useMemo(
      () => ({
        handleMyBagAppearedSoKickoffTheInitialLoad,
      }),
      [handleMyBagAppearedSoKickoffTheInitialLoad]
    );

  return (
    <MyBagInitializationContext.Provider value={value}>
      <MyBagInitialization_ControllerContext.Provider value={controllerValue}>
        {props.children || []}
      </MyBagInitialization_ControllerContext.Provider>
    </MyBagInitializationContext.Provider>
  );
};

export const useMyBagInitialization = () => {
  const value = React.useContext(MyBagInitializationContext);
  if (value === defaultValue) {
    throw new Error(
      "useMyBagInitialization must be used within a MyBagInitializationProvider"
    );
  }
  return value;
};

export const useMyBagInitialization_asController = () => {
  const value = React.useContext(MyBagInitialization_ControllerContext);
  if (value === defaultValue_controller) {
    throw new Error(
      "useMyBagInitialization_asController must be used within a MyBagInitializationProvider"
    );
  }
  return value;
};

export const useMyBagOnThisPage = () => {
  const { isReady } = useMyBagInitialization();
  const { handleMyBagAppearedSoKickoffTheInitialLoad } =
    useMyBagInitialization_asController();

  React.useEffect(() => {
    if (isReady) {
      handleMyBagAppearedSoKickoffTheInitialLoad();
    }
  }, [isReady, handleMyBagAppearedSoKickoffTheInitialLoad]);
};
